import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECTS, LEVELS, subjectStyle, SUBJECT_DOT } from "@/lib/constants";
import { Markdown } from "@/components/Markdown";
import { Plus, Trash2, X, BookMarked, Sparkles, CheckCircle2, Clock, Pencil, Lock, Zap, Download, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const empty = { subject: "Mathématiques", chapter: "", title: "", content: "" };

export default function Revisions() {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ subject: "Mathématiques", chapter: "" });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mine");
  const [libSheets, setLibSheets] = useState([]);
  const [libFilters, setLibFilters] = useState({ subject: "all", level: user?.level || "all" });
  const [libLoading, setLibLoading] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [quizDone, setQuizDone] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isPremium = user?.plan === "premium";

  const load = () => api.get("/sheets").then(({ data }) => setSheets(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab !== "library") return;
    setLibLoading(true);
    const params = new URLSearchParams(libFilters).toString();
    api.get(`/library/sheets?${params}`).then(({ data }) => setLibSheets(data)).finally(() => setLibLoading(false));
  }, [tab, libFilters]);

  const copySheet = async (lib) => {
    try {
      await api.post(`/library/sheets/${lib.id}/copy`);
      toast.success("Fiche ajoutée à tes révisions !");
      setViewing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/sheets/${editing.id}`, form);
        toast.success("Fiche mise à jour");
      } else {
        await api.post("/sheets", form);
        toast.success("Fiche créée");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ subject: s.subject, chapter: s.chapter, title: s.title, content: s.content });
    setViewing(null);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await api.delete(`/sheets/${id}`);
      toast.success("Fiche supprimée");
      setViewing(null);
      setConfirmDelete(false);
      setSheets(sheets.filter((s) => s.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const review = async (id) => {
    try {
      await api.post(`/sheets/${id}/review`, { minutes: 15 });
      toast.success("+15 min de révision enregistrées !");
      if (viewing?.id === id) {
        setViewing({ ...viewing, last_reviewed: new Date().toISOString() });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const generateAi = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/ai/generate-sheet", aiForm);
      setAiResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  const saveAiSheet = async () => {
    try {
      await api.post("/sheets", { subject: aiResult.subject, chapter: aiResult.chapter, title: aiResult.title, content: aiResult.content });
      toast.success("Fiche IA enregistrée dans tes révisions");
      setAiOpen(false);
      setAiResult(null);
      setAiForm({ subject: "Mathématiques", chapter: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startQuiz = async (sheet) => {
    setQuizOpen(true);
    setQuiz(null);
    setAnswers({});
    setQuizDone(false);
    setQuizLoading(true);
    try {
      const { data } = await api.post("/ai/quiz", { sheet_id: sheet.id, source: sheet._lib ? "library" : "mine" });
      setQuiz(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setQuizOpen(false);
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    setQuizDone(true);
    const score = quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);
    try {
      await api.post("/quiz/result", { sheet_id: viewing.id, score, total: quiz.questions.length });
    } catch {
      toast.error("Score non enregistré — les XP du quiz ne seront pas comptés.");
    }
  };

  const filtered = sheets.filter((s) =>
    (filter === "all" || s.subject === filter) &&
    (search === "" || (s.title + s.chapter).toLowerCase().includes(search.toLowerCase()))
  );

  const chapters = [...new Set(sheets.filter((s) => filter === "all" || s.subject === filter).map((s) => s.chapter))];

  return (
    <div className="space-y-6" data-testid="revisions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Fiches de révision</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1">{sheets.length} fiche{sheets.length > 1 ? "s" : ""} classée{sheets.length > 1 ? "s" : ""} par matière et chapitre.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => downloadPdf("/export/sheets.pdf", "mes-fiches-proedu.pdf").then(() => toast.success("PDF téléchargé !")).catch(() => toast.error("Export impossible"))}
            data-testid="export-sheets-pdf"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200"
          >
            <Download className="h-4 w-4" /> PDF
          </button>
          <Link
            to="/flashcards"
            data-testid="goto-flashcards"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold hover:bg-amber-200 transition-colors duration-200"
          >
            <Zap className="h-4 w-4" /> Flashcards
          </Link>
          <button
            onClick={() => (isPremium ? setAiOpen(true) : toast.info("Passe à Premium pour générer des fiches par IA !"))}
            data-testid="ai-generate-sheet-button"
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-colors duration-200 ${
              isPremium ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90" : "bg-slate-100 text-slate-500"
            }`}
          >
            {isPremium ? <Sparkles className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            Générer avec l'IA
          </button>
          <button
            onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
            data-testid="add-sheet-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-4 w-4" /> Nouvelle fiche
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 p-1 bg-white border border-slate-200 rounded-full w-fit" data-testid="revisions-tabs">
        <button
          onClick={() => setTab("mine")}
          data-testid="tab-my-sheets"
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${tab === "mine" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        >
          Mes fiches
        </button>
        <button
          onClick={() => setTab("library")}
          data-testid="tab-library"
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${tab === "library" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        >
          Bibliothèque officielle
        </button>
      </div>

      {tab === "mine" && (
      <>
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="sheet-search-input"
          placeholder="Rechercher une fiche…"
          className="px-4 py-2.5 rounded-full border border-slate-200 text-sm bg-white w-full sm:w-64"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sheet-subject-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
          <option value="all">Toutes les matières</option>
          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {chapters.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="sheet-chapters">
            {chapters.map((c) => (
              <button key={c} onClick={() => setSearch(c)} data-testid={`sheet-chapter-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-700 transition-colors duration-200">
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-3xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="sheets-empty">
          <BookMarked className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucune fiche ici.</p>
          <p className="text-sm text-slate-400 mt-1">Crée ta première fiche ou génère-la avec l'IA (Premium).</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => setViewing(s)}
              data-testid={`sheet-card-${s.id}`}
              className="relative text-left rounded-3xl border p-5 pt-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200 overflow-hidden"
              style={{
                backgroundColor: `${SUBJECT_DOT[s.subject] || "#64748B"}12`,
                borderColor: `${SUBJECT_DOT[s.subject] || "#64748B"}38`,
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-1.5"
                style={{ background: `linear-gradient(to right, ${SUBJECT_DOT[s.subject] || "#64748B"}, ${SUBJECT_DOT[s.subject] || "#64748B"}55)` }}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(s.subject)}`}>{s.subject}</span>
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${SUBJECT_DOT[s.subject] || "#64748B"}1F`, color: SUBJECT_DOT[s.subject] || "#475569" }}
                >{s.chapter}</span>
              </div>
              <h3 className="mt-3 font-display text-base font-semibold text-slate-900 tracking-tight line-clamp-2">{s.title}</h3>
              <p className="mt-1.5 text-xs text-slate-500 line-clamp-2 whitespace-pre-line">{s.content}</p>
              <div className="mt-3 flex items-center gap-3 text-[11px] font-medium" style={{ color: `${SUBJECT_DOT[s.subject] || "#64748B"}CC` }}>
                {s.review_count > 0 && <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Révisée {s.review_count}×</span>}
                {s.last_reviewed && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(s.last_reviewed).toLocaleDateString("fr-FR")}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      </>
      )}

      {tab === "library" && (
        <div className="space-y-6" data-testid="library-section">
          <div className="flex flex-wrap gap-3 items-center">
            <select value={libFilters.subject} onChange={(e) => setLibFilters({ ...libFilters, subject: e.target.value })} data-testid="library-subject-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
              <option value="all">Toutes les matières</option>
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={libFilters.level} onChange={(e) => setLibFilters({ ...libFilters, level: e.target.value })} data-testid="library-level-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
              <option value="all">Tous les niveaux</option>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <span className="text-xs text-slate-400">Fiches alignées sur les programmes de l'Éducation nationale</span>
          </div>
          {libLoading ? (
            <div className="animate-pulse grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-3xl" />)}</div>
          ) : libSheets.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="library-empty">
              <BookMarked className="h-10 w-10 text-indigo-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Aucune fiche pour ces filtres.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {libSheets.map((s) => (
                <button
                  key={s.id}
                  onClick={() => s.locked
                    ? toast.info(`Cette fiche est réservée à la formule ${s.tier === "premium" ? "Premium" : "Basique"} !`)
                    : setViewing({ ...s, _lib: true })}
                  data-testid={`library-card-${s.id}`}
                  className="relative text-left rounded-3xl border p-5 pt-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200 overflow-hidden"
                  style={{
                    backgroundColor: `${SUBJECT_DOT[s.subject] || "#64748B"}12`,
                    borderColor: `${SUBJECT_DOT[s.subject] || "#64748B"}38`,
                  }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1.5"
                    style={{ background: `linear-gradient(to right, ${SUBJECT_DOT[s.subject] || "#64748B"}, ${SUBJECT_DOT[s.subject] || "#64748B"}55)` }}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(s.subject)}`}>{s.subject}</span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/70 text-slate-600">{s.level}</span>
                    {s.tier !== "gratuit" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1">
                        <Lock className="h-3 w-3" /> {s.tier === "premium" ? "Premium" : "Basique"}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-900 tracking-tight line-clamp-2">{s.title}</h3>
                  <p className="mt-1 text-xs text-slate-500">{s.chapter}</p>
                  {s.locked && (
                    <div className="absolute inset-0 rounded-3xl bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2" data-testid={`library-locked-${s.id}`}>
                      <Lock className="h-6 w-6 text-slate-400" />
                      <Link to="/pricing" onClick={(e) => e.stopPropagation()} data-testid={`library-upgrade-${s.id}`} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline">
                        Débloquer avec {s.tier === "premium" ? "Premium" : "Basique"}
                      </Link>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* View sheet modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="sheet-view-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-fade-in-up">
            <div
              className="flex items-start justify-between p-6 border-b border-slate-100"
              style={{ background: `linear-gradient(135deg, ${SUBJECT_DOT[viewing.subject] || "#64748B"}1A, ${SUBJECT_DOT[viewing.subject] || "#64748B"}08)` }}
            >
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(viewing.subject)}`}>{viewing.subject}</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{viewing.chapter}</span>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mt-2" data-testid="sheet-view-title">{viewing.title}</h2>
              </div>
              <button onClick={() => setViewing(null)} data-testid="sheet-view-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div
              className="p-6 sm:p-8 overflow-y-auto flex-1"
              style={{ backgroundColor: `${SUBJECT_DOT[viewing.subject] || "#64748B"}0D` }}
            >
              <div
                className="max-w-none bg-white rounded-2xl border p-6 shadow-sm"
                style={{ borderColor: `${SUBJECT_DOT[viewing.subject] || "#64748B"}2E` }}
                data-testid="sheet-view-content"
              >
                <Markdown text={viewing.content} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 p-6 border-t border-slate-100">
              <button
                onClick={() => (isPremium ? startQuiz(viewing) : toast.info("Passe à Premium pour générer des quiz par IA !"))}
                data-testid="sheet-quiz-button"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  isPremium ? "bg-violet-100 text-violet-700 hover:bg-violet-200" : "bg-slate-100 text-slate-500"
                }`}
              >
                {isPremium ? <HelpCircle className="h-4 w-4" /> : <Lock className="h-4 w-4" />} Quiz IA
              </button>
              {viewing._lib ? (
                <button onClick={() => copySheet(viewing)} data-testid="library-copy-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200">
                  <Plus className="h-4 w-4" /> Ajouter à mes fiches
                </button>
              ) : (
                <>
                  {(() => {
                    const reviewedToday = (viewing.last_reviewed || "").slice(0, 10) === new Date().toISOString().slice(0, 10);
                    return (
                      <button
                        onClick={() => review(viewing.id)}
                        disabled={reviewedToday}
                        data-testid="sheet-review-button"
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                          reviewedToday ? "bg-slate-100 text-slate-400 cursor-default" : "bg-emerald-500 text-white hover:bg-emerald-600"
                        }`}
                      >
                        <CheckCircle2 className="h-4 w-4" /> {reviewedToday ? "Déjà révisée aujourd'hui" : "Marquer révisée (+15 min)"}
                      </button>
                    );
                  })()}
                  <button onClick={() => openEdit(viewing)} data-testid="sheet-edit-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200">
                    <Pencil className="h-4 w-4" /> Modifier
                  </button>
                  <button onClick={() => remove(viewing.id)} data-testid="sheet-delete-button" className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${confirmDelete ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-rose-50 text-rose-600 hover:bg-rose-100"}`}>
                    <Trash2 className="h-4 w-4" /> {confirmDelete ? "Confirmer la suppression ?" : "Supprimer"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create / edit modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="sheet-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-7 max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? "Modifier la fiche" : "Nouvelle fiche"}</h2>
              <button onClick={() => { setOpen(false); setEditing(null); }} data-testid="sheet-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                  <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="sheet-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                    {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Chapitre</label>
                  <input type="text" required value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} data-testid="sheet-chapter-input" placeholder="Ex : Les fractions" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Titre</label>
                <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sheet-title-input" placeholder="Ex : Additionner des fractions" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Contenu</label>
                <textarea required rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="sheet-content-input" placeholder="Définitions, formules, exemples, pièges à éviter…" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm resize-y" />
              </div>
              <button type="submit" data-testid="sheet-submit-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                {editing ? "Enregistrer les modifications" : "Créer la fiche"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* AI generation modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="ai-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" /> Génération de fiche par IA
              </h2>
              <button onClick={() => { setAiOpen(false); setAiResult(null); }} data-testid="ai-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={generateAi} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                <select value={aiForm.subject} onChange={(e) => setAiForm({ ...aiForm, subject: e.target.value })} data-testid="ai-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Chapitre</label>
                <input type="text" required value={aiForm.chapter} onChange={(e) => setAiForm({ ...aiForm, chapter: e.target.value })} data-testid="ai-chapter-input" placeholder="Ex : La Révolution française" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <button type="submit" disabled={aiLoading} data-testid="ai-generate-submit" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 whitespace-nowrap">
                {aiLoading ? "Génération…" : "Générer"}
              </button>
            </form>
            {aiLoading && (
              <div className="mt-6 space-y-3 animate-pulse" data-testid="ai-loading">
                {[...Array(5)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded-full" style={{ width: `${90 - i * 12}%` }} />)}
              </div>
            )}
            {aiResult && (
              <div className="mt-6" data-testid="ai-result">
                <div className="rounded-2xl border border-violet-200 bg-white p-6 max-h-96 overflow-y-auto shadow-sm">
                  <Markdown text={aiResult.content} />
                </div>
                <button onClick={saveAiSheet} data-testid="ai-save-sheet-button" className="mt-4 w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                  Enregistrer cette fiche
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Quiz IA modal */}
      {quizOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="quiz-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in-up">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-indigo-600" /> Quiz IA{quiz ? ` — ${quiz.title}` : ""}
              </h2>
              <button onClick={() => setQuizOpen(false)} data-testid="quiz-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {quizLoading && (
                <div className="space-y-3 animate-pulse" data-testid="quiz-loading">
                  {[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded-full" style={{ width: `${85 - i * 15}%` }} />)}
                  <p className="text-xs text-slate-400 pt-2">L'IA prépare tes questions…</p>
                </div>
              )}
              {quiz && (
                <div className="space-y-6">
                  {quiz.questions.map((q, i) => (
                    <div key={i} data-testid={`quiz-question-${i}`}>
                      <p className="text-sm font-semibold text-slate-900 mb-2.5">{i + 1}. {q.question}</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {q.choices.map((c, j) => {
                          const chosen = answers[i] === j;
                          let cls = "border-slate-200 bg-white hover:border-indigo-300 text-slate-700";
                          if (quizDone) {
                            if (j === q.answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
                            else if (chosen) cls = "border-rose-300 bg-rose-50 text-rose-700";
                          } else if (chosen) {
                            cls = "border-indigo-500 bg-indigo-50 text-indigo-800";
                          }
                          return (
                            <button
                              key={j}
                              type="button"
                              disabled={quizDone}
                              onClick={() => setAnswers({ ...answers, [i]: j })}
                              data-testid={`quiz-choice-${i}-${j}`}
                              className={`text-left px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors duration-150 ${cls}`}
                            >
                              {c}
                            </button>
                          );
                        })}
                      </div>
                      {quizDone && q.explanation && (
                        <p className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2" data-testid={`quiz-explanation-${i}`}>{q.explanation}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {quiz && (
              <div className="p-6 border-t border-slate-100">
                {quizDone ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-lg font-bold text-slate-900" data-testid="quiz-score">
                      Score : {quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0)} / {quiz.questions.length}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => startQuiz(viewing)} data-testid="quiz-restart" className="px-5 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200">
                        Nouveau quiz
                      </button>
                      <button onClick={() => setQuizOpen(false)} data-testid="quiz-finish" className="px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200">
                        Terminer
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={submitQuiz}
                    disabled={Object.keys(answers).length < quiz.questions.length}
                    data-testid="quiz-submit-button"
                    className="w-full py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                  >
                    Valider mes réponses ({Object.keys(answers).length}/{quiz.questions.length})
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
